import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const num = (v, int) => (v === "" ? "" : int ? parseInt(v, 10) : parseFloat(v));

export const MonthsEditor = ({ months, onChange }) => {
  const upd = (i, k, v) => onChange(months.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const total = months.reduce((s, m) => s + (parseInt(m.days, 10) || 0), 0);
  return (
    <div className="space-y-2" data-testid="months-editor">
      <div className="flex items-center justify-between"><Label>Months</Label><span className="font-mono-lf text-xs text-[#A8A090]" data-testid="months-total">{months.length} months · {total} days</span></div>
      <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1 lf-scroll">
        {months.map((m, i) => (
          <div key={i} className="grid grid-cols-[1fr_84px_32px] gap-2">
            <Input value={m.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder={`Month ${i + 1}`} data-testid={`month-name-${i}`} />
            <Input type="number" min={1} className="font-mono-lf" value={m.days} onChange={(e) => upd(i, "days", num(e.target.value, true))} data-testid={`month-days-${i}`} />
            <Button type="button" size="icon" variant="ghost" disabled={months.length === 1} onClick={() => onChange(months.filter((_, j) => j !== i))} data-testid={`month-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <Button type="button" size="sm" variant="ghost" onClick={() => onChange([...months, { name: `Month ${months.length + 1}`, days: months.at(-1)?.days || 30 }])} data-testid="month-add-button"><Plus className="mr-1 h-4 w-4" />Add month</Button>
    </div>
  );
};

export const MoonsEditor = ({ moons, onChange }) => {
  const upd = (i, k, v) => onChange(moons.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  return (
    <div className="space-y-2" data-testid="moons-editor">
      <Label>Moons <span className="normal-case text-[#A8A090]">(cycle in local days · offset = a day with a new moon)</span></Label>
      {moons.map((m, i) => (
        <div key={i} className="grid grid-cols-[1fr_90px_90px_32px] gap-2">
          <Input value={m.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Moon name" data-testid={`moon-name-${i}`} />
          <Input type="number" step="any" className="font-mono-lf" value={m.cycle} onChange={(e) => upd(i, "cycle", num(e.target.value))} title="Cycle (days)" data-testid={`moon-cycle-${i}`} />
          <Input type="number" step="any" className="font-mono-lf" value={m.offset} onChange={(e) => upd(i, "offset", num(e.target.value))} title="Offset" data-testid={`moon-offset-${i}`} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(moons.filter((_, j) => j !== i))} data-testid={`moon-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="ghost" onClick={() => onChange([...moons, { name: `Moon ${moons.length + 1}`, cycle: 29.5, offset: 0 }])} data-testid="moon-add-button"><Plus className="mr-1 h-4 w-4" />Add moon</Button>
    </div>
  );
};

export const LinkEditor = ({ link, timelines, onChange }) => (
  <div className="space-y-2" data-testid="link-editor">
    <Label>Linked timeline</Label>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_110px]">
      <Select value={link?.timeline_id || "none"} onValueChange={(v) => onChange(v === "none" ? null : { unit: "year", offset: 0, ...link, timeline_id: v })}>
        <SelectTrigger data-testid="link-timeline-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— Not linked —</SelectItem>
          {timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {link && (
        <>
          <Select value={link.unit} onValueChange={(v) => onChange({ ...link, unit: v })}>
            <SelectTrigger data-testid="link-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="year">1 T = 1 year</SelectItem><SelectItem value="day">1 T = 1 day</SelectItem></SelectContent>
          </Select>
          <Input type="number" className="font-mono-lf" value={link.offset} onChange={(e) => onChange({ ...link, offset: num(e.target.value, true) || 0 })} title="Offset" data-testid="link-offset-input" />
        </>
      )}
    </div>
    {link && <p className="text-xs text-[#A8A090]">{link.unit === "year" ? `T0 = year ${link.offset}` : `T0 = local day #${link.offset} counted from the epoch`}. Timeline events appear on this calendar and show its dates.</p>}
  </div>
);
